import { useTranslation } from 'react-i18next';
import { ChevronDownIcon } from '@utils/icons';
import type { TFunction } from 'i18next';
import type { IElementGroup, IElementMember, IElementSection, ITagPart } from '../../stock/claimElements';
import styles from './StockElementPicker.module.css';

/** A group's identity across re-renders and filtering: its band (`scope`),
 *  its section and its quantity, which is what folded its members together. */
const groupKey = (scope: string, section: IElementSection, group: IElementGroup): string =>
  `${scope}${String(section.typeName)}|${String(group.qty)}`;

/** `#{tag}`, the Revit id — the GlobalId when the element carries no tag —
 *  and "· 2 of 3" behind it when the id is shared by the pieces of a split
 *  element (`tagParts`), so three rows never read as the same slab thrice. */
function idOf(member: IElementMember, parts: ReadonlyMap<string, ITagPart>, t: TFunction): string {
  const id = `#${member.el.tag ?? member.el.globalId}`;
  const part = parts.get(member.el.globalId);
  return part === undefined ? id : t('scheduler.panel.stock.partOf', { id, index: part.index, count: part.count });
}

interface IMemberRowProps {
  member: IElementMember;
  /** The first line: the kind for a singleton, the Revit id for a group member. */
  title: string;
  /** The Revit id under a singleton's kind; nothing under a group member. */
  sub: string | null;
  /** The right-hand figure — a singleton's quantity. */
  trailing: string | null;
  indent: boolean;
  onToggle: (globalId: string) => void;
}

/** One element: a checkbox row, dimmed and inert when another task holds it. */
function MemberRow({ member, title, sub, trailing, indent, onToggle }: IMemberRowProps) {
  const { t } = useTranslation();
  const takenBy = member.taken;
  return (
    <label className={[styles.row, indent ? styles.child : '', takenBy === null ? '' : styles.takenRow].join(' ')}>
      <input
        type="checkbox"
        checked={member.chosen}
        disabled={takenBy !== null}
        onChange={() => onToggle(member.el.globalId)}
      />
      <div>
        <div className={styles.line1}>{title}</div>
        {(sub !== null || takenBy !== null) && (
          <div className={styles.line2}>
            {sub !== null && <span className={styles.rid}>{sub}</span>}
            {takenBy !== null && <span className={styles.who}>{t('scheduler.panel.stock.takenBy', { task: takenBy })}</span>}
          </div>
        )}
      </div>
      <div className={styles.qty}>{trailing}</div>
    </label>
  );
}

interface IGroupRowProps {
  group: IElementGroup;
  kind: string;
  unitLabel: string;
  open: boolean;
  onToggleOpen: () => void;
  onToggleGroup: () => void;
  /** The counter: how many of the group's free members to choose. */
  onCount: (count: number) => void;
}

/** Elements sharing one quantity, folded: a tri-state checkbox over the FREE
 *  members, a chevron that expands them, and the shared figure. */
function GroupRow({ group, kind, unitLabel, open, onToggleOpen, onToggleGroup, onCount }: IGroupRowProps) {
  const { t } = useTranslation();
  const free = group.members.filter((m) => m.taken === null);
  const chosen = free.filter((m) => m.chosen).length;
  const taken = group.members.length - free.length;
  const all = free.length > 0 && chosen === free.length;
  const indeterminate = chosen > 0 && !all;
  return (
    <div
      className={`${styles.row} ${styles.groupRow}`}
      role="button"
      tabIndex={0}
      aria-expanded={open}
      onClick={onToggleOpen}
      onKeyDown={(e) => {
        // Only the row's own keys: Space on the checkbox inside it is the
        // checkbox's, and must not also fold the group.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleOpen();
        }
      }}
    >
      <input
        type="checkbox"
        // `indeterminate` is a property, not an attribute — set it on the
        // node. A callback ref runs on every render with the current value
        // (and with null on unmount, which needs nothing).
        ref={(el) => { if (el) el.indeterminate = indeterminate; }}
        checked={all}
        disabled={free.length === 0}
        aria-label={`${kind} × ${group.members.length}`}
        onClick={(e) => e.stopPropagation()}
        onChange={onToggleGroup}
      />
      <div>
        <div className={styles.line1}>
          {kind}
          <span className={styles.pill}>× {group.members.length}</span>
          <span className={styles.caret} data-collapsed={!open}>
            <ChevronDownIcon size={14} />
          </span>
        </div>
        <div className={styles.line2}>
          {t('scheduler.panel.stock.chosenOf', { chosen, free: free.length })}
          {taken > 0 && ` · ${t('scheduler.panel.stock.takenCount', { count: taken })}`}
        </div>
      </div>
      <div className={styles.qty}>
        <input
          type="number"
          className={styles.counter}
          min={0}
          max={free.length}
          value={chosen}
          disabled={free.length === 0}
          aria-label={t('scheduler.panel.stock.groupCount', { kind })}
          // The row folds on click; the counter is its own control.
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          // Selecting on focus lets the next keystroke replace the count
          // instead of appending to it (typing 3 into "0" reads 03 otherwise).
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => onCount(Number(e.target.value) || 0)}
        />
        {group.qty === null ? '—' : t('scheduler.panel.stock.each', { qty: group.qty, unit: unitLabel })}
      </div>
    </div>
  );
}

interface IPickerSectionProps {
  /** Prefix for the group keys — the floor band's, '' when the list has
   *  none — so the same fold on two floors opens independently. */
  scope: string;
  section: IElementSection;
  kind: string;
  unitLabel: string;
  /** Part numbers for split elements sharing a Revit id — see `tagParts`. */
  parts: ReadonlyMap<string, ITagPart>;
  /** The header folds the section only when the material comes in more than
   *  one type; a single type keeps its header as a plain label. */
  collapsible: boolean;
  sectionOpen: boolean;
  onToggleSection: () => void;
  open: ReadonlySet<string>;
  onToggleOpen: (key: string) => void;
  onToggle: (globalId: string) => void;
  onToggleGroup: (group: IElementGroup) => void;
  onCount: (group: IElementGroup, count: number) => void;
}

/** One `typeName`'s rows — a singleton as a plain element row, a group as a
 *  fold with its members under it when open — behind a folding header when
 *  the list has several types. */
export function PickerSection({
  scope,
  section,
  kind,
  unitLabel,
  parts,
  collapsible,
  sectionOpen,
  onToggleSection,
  open,
  onToggleOpen,
  onToggle,
  onToggleGroup,
  onCount,
}: IPickerSectionProps) {
  const { t } = useTranslation();
  return (
    <>
      {collapsible ? (
        <button type="button" className={styles.section} aria-expanded={sectionOpen} onClick={onToggleSection}>
          <span>{t('scheduler.panel.stock.sectionLine', { type: section.typeName ?? kind, count: section.count })}</span>
          <span className={styles.caret} data-collapsed={!sectionOpen}>
            <ChevronDownIcon size={14} />
          </span>
        </button>
      ) : (
        <div className={styles.section}>
          <span>{t('scheduler.panel.stock.sectionLine', { type: section.typeName ?? kind, count: section.count })}</span>
        </div>
      )}
      {(!collapsible || sectionOpen) && section.groups.map((group) => {
        const key = groupKey(scope, section, group);
        if (group.members.length === 1) {
          const member = group.members[0]!;
          return (
            <MemberRow
              key={key}
              member={member}
              title={kind}
              sub={idOf(member, parts, t)}
              trailing={group.qty === null ? '—' : `${group.qty} ${unitLabel}`}
              indent={false}
              onToggle={onToggle}
            />
          );
        }
        const isOpen = open.has(key);
        return (
          <div key={key}>
            <GroupRow
              group={group}
              kind={kind}
              unitLabel={unitLabel}
              open={isOpen}
              onToggleOpen={() => onToggleOpen(key)}
              onToggleGroup={() => onToggleGroup(group)}
              onCount={(count) => onCount(group, count)}
            />
            {isOpen &&
              group.members.map((member) => (
                <MemberRow
                  key={member.el.globalId}
                  member={member}
                  title={idOf(member, parts, t)}
                  sub={null}
                  trailing={null}
                  indent
                  onToggle={onToggle}
                />
              ))}
          </div>
        );
      })}
    </>
  );
}
